<template>
  <div class="p-2 sm:p-3">
    <div class="flex justify-center flex-wrap">
      <!-- Success box -->
      <div
        v-if="isSuccess"
        class="bg-white rounded-lg shadow-card px-4 py-8 sm:p-12 max-w-xl"
      >
        <div class="mb-12 text-center flex justify-center">
          <h1 class="font-semibold text-2xl lg:text-3xl">Success</h1>
        </div>
        <p class="leading-loose text-center mb-16">
          Your menu was saved successfully!
        </p>
        <div class="flex justify-center">
          <router-link :to="`/caterer/dashboard`">
            <button class="btn btn-primary font-semibold rounded-lg px-16">
              Go to Home Page
            </button>
          </router-link>
        </div>
      </div>
      <!-- ### -->

      <!-- Login or Signup -->
      <div v-show="needAuth" slot="modal-wrapper">
        <div class="max-w-xl min-w-full sm:min-w-30 m-2 mx-auto p-2">
          <transition name="flip" mode="out-in">
            <!-- Login -->
            <div v-if="authMethod === 'login'" :key="'login'">
              <SignIn @forgot="authMethod = 'forgot'" @success="onLoginSuccess">
                <span slot="header-title">
                  Login To Continue!
                </span>
                <span slot="button-title">
                  Login & Continue
                </span>
                <div slot="footer" class="text-center mt-5 mb-5">
                  <button
                    class="btn btn-secondary rounded-lg block w-full"
                    @click="showAuthModal = false"
                  >
                    Cancel
                  </button>
                  <a
                    href="javascript:"
                    class="text-primary inline-block mt-8"
                    @click="authMethod = 'signup'"
                  >
                    Don't have account? SignUp!!!
                  </a>
                </div>
              </SignIn>
            </div>

            <!-- Signup -->
            <div v-if="authMethod === 'signup'" :key="'signup'">
              <SignUp @success="onLoginSuccess" :updateUserID="userid">
                <span slot="header-title">
                  Signup To Continue!
                </span>
                <span slot="button-title">
                  Signup & Continue
                </span>
                <div slot="footer" class="text-center mt-5 mb-5">
                  <button
                    class="btn btn-secondary rounded-lg block w-full"
                    @click="authMethod = 'login'"
                  >
                    Cancel
                  </button>
                  <a
                    href="javascript:"
                    class="text-primary inline-block mt-8"
                    @click="authMethod = 'login'"
                  >
                    I already have account!!!
                  </a>
                </div>
              </SignUp>
            </div>

            <!-- ForgotPassword -->
            <div v-if="authMethod === 'forgot'" :key="'forgot'">
              <ForgotPassword @success="authMethod = 'login'">
                <div slot="footer" class="text-center mt-5 mb-5">
                  <button
                    class="btn btn-secondary rounded-lg block w-full"
                    @click="authMethod = 'login'"
                  >
                    Cancel
                  </button>
                </div>
              </ForgotPassword>
            </div>
          </transition>
        </div>
      </div>

      <!-- Locations Checkout Modal -->
      <checkout-locations-modal
        @skip="skipedPayment = true"
        @back="$emit('back')"
        @success="onCheckoutSuccess"
        @removeLocation="onRemoveLocation"
        v-if="showCheckout"
        :total="price"
        :priceDetails="priceDetails"
        :catererId="getCatererDetails.id"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import ForgotPassword from "../../../components/common/auth/ForgotPassword.vue";
import SignIn from "../../../components/common/auth/SignIn.vue";
import Api from "@/api/api_v1.0";
import SignUp from "../../../components/common/auth/SignUp.vue";
import CheckoutLocationsModal from "@/components/caterer/modal/CheckoutLocationsModal.vue";

export default {
  components: { SignIn, ForgotPassword, SignUp, CheckoutLocationsModal },
  data() {
    return {
      skipedPayment: false,
      authMethod: "signup",
      completed: false,
      showPriceListModal: false,
      price: null,
      priceDetails: []
    };
  },
  computed: {
    ...mapGetters(["isAuthenticated", "getCatererDetails", "personalDetails"]),



    needAuth() {
      return !this.isAuthenticated || this.isSignupUncompleted;
    },

    isSignupUncompleted() {
      return this.isAuthenticated && !this.personalDetails.iscompleted
    },

    userid() {
      return this.isAuthenticated ? this.personalDetails.id : false
    },

    isSuccess() {
      return (
        (this.price == 0 && !this.needAuth) ||
        (this.skipedPayment && !this.needAuth)
      );
    },

    showCheckout() {
      return this.price > 0 && !this.needAuth && !this.skipedPayment;
    },

    catererUUID() {
      return (this.$route.params.uuid && this.$route.params.uuid !== 'new') ?? false
    },
  },

  mounted() {
    let timer = setInterval(() => {
      if (this.getCatererDetails !== null) {
        this.getLocationsPrice();
        clearInterval(timer);
      }
    });
  },

  beforeCreate() {
    this.$root.$on("eventPlannerProcessSaveExit", () => {
      this.$router.push("/caterer/dashboard");
    });
  },
  beforeDestroy() {
    this.$root.$off("eventPlannerProcessSaveExit", () => {
      this.$router.push("/caterer/dashboard");
    });
  },

  methods: {
    onCheckoutSuccess() {
      this.price = 0;
      this.priceDetails = [];

      this.$gtm.trackEvent({
        event: "caterer_pays_locations",
        category: "Caterer",
        action: "catererPaysLocations",
        label: "Caterer Payes for Locations",
        value: null
      });
    },

    async onLoginSuccess() {
      await this.tryLinkCatererToUser();

      // check for unpaid locations
      this.getLocationsPrice();

      this.completed = true;
    },

    async tryLinkCatererToUser() {
      if (this.isAuthenticated) {
        await Api.linkCatererToUser( this.getCatererDetails.id );
        this.$store.dispatch("getPersonalDetails");
        this.callGtagCatererEvent();
      }
    },

    callGtagCatererEvent() {
      if (!this.catererUUID) {
        this.$gtm.trackEvent({
          event: "caterer_registered",
          category: "Caterer",
          action: "catererRegistered",
          label: "New Caterer Registered",
          value: this.personalDetails.email
        });
      }
    },

    parseLocations(data) {
      return data.map(zip => {
        return zip.text;
      });
    },

    async getLocationsPrice() {
      const locations = await this.getCatererDetails?.service_zip_codes;

      const data = {
        locations: this.parseLocations(locations),
        caterer_id: this.getCatererDetails.id
      };

      const response = await Api.getLocationsPrice(data);

      this.price = response.data.total;
      this.priceDetails = response.data.prices;
    },

    async onRemoveLocation(location) {
      await Api.removeCatererLocation(this.getCatererDetails.id, location);

      let locations = this.getCatererDetails.service_zip_codes;

      const index = locations.indexOf(
        locations.find(item => {
          return item.text == location;
        })
      );

      locations.splice(index, 1);

      let caterer = this.getCatererDetails;
      caterer.service_zip_codes = locations;

      this.$store.commit("setCatererDetails", caterer);

      this.getLocationsPrice();
    },
  }
};
</script>
